/**
 * What THIS person changed on a board, field by field.
 *
 * A team board is edited by several people at once. Saving the whole board
 * from one person's screen (syncRecordScope) sent that screen's copy of every
 * card — including cards a teammate had just renamed, moved or created, whose
 * news had not reached this screen yet — and tombstoned every record the
 * screen did not hold. Found by e2e/demo/teamCollabBoards.check.js (24.10.2026):
 * titles typed by teammates reverted to empty (B3), and a card moved to Done
 * jumped back because the person editing its description re-sent its old list
 * (B2).
 *
 * So the board remembers what its own user did — each `updateBoard` and each
 * undo/redo diffs the board before and after AT RECORD LEVEL (the records
 * `decomposeBoard` saves) — and the save writes exactly that: the records this
 * person created, the fields this person changed, the records this person
 * deleted. The server already merges a push field by field (sync.ex
 * build_changes: Map.merge(row.data, set)), so two people changing different
 * fields of one card both win, and the last of two people changing the SAME
 * field wins that field only.
 *
 * Shape: Map<recordId, { created: bool, deleted: bool, fields: Set<string> }>.
 * `fields` holds data keys plus the record meta 'parent_id' / 'sort_key'.
 * Pure and import-free: callers pass record lists (boardsStore.decomposeBoard).
 */
const META_FIELDS = ['parent_id', 'sort_key'];

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const byId = (records) => new Map((records || []).map((r) => [r.id, r]));

/** The fields of one record that differ between two versions of it. */
export function changedFields(before, after) {
  const fields = new Set();
  const a = before.data || {};
  const b = after.data || {};
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (!same(a[k], b[k])) fields.add(k);
  }
  for (const m of META_FIELDS) {
    if (!same(before[m] ?? null, after[m] ?? null)) fields.add(m);
  }
  if (before.record_type !== after.record_type) fields.add('record_type');
  return fields;
}

/** What changed from records `prevRecords` to `nextRecords` (one board each), record by record. */
export function diffRecords(prevRecords, nextRecords) {
  const out = new Map();
  if (!prevRecords || !nextRecords || prevRecords === nextRecords) return out;
  const a = byId(prevRecords);
  const b = byId(nextRecords);
  for (const [id, after] of b) {
    const before = a.get(id);
    if (!before) out.set(id, { created: true, deleted: false, fields: new Set() });
    else {
      const fields = changedFields(before, after);
      if (fields.size) out.set(id, { created: false, deleted: false, fields });
    }
  }
  for (const id of a.keys()) if (!b.has(id)) out.set(id, { created: false, deleted: true, fields: new Set() });
  return out;
}

/** Fold change set `more` (later) into `into` (earlier), in place. Returns `into`. */
export function mergeChanges(into, more) {
  for (const [id, ch] of more) {
    const had = into.get(id);
    if (!had) {
      into.set(id, { created: ch.created, deleted: ch.deleted, fields: new Set(ch.fields) });
      continue;
    }
    if (ch.deleted) {
      // Created and deleted before either was saved: there is nothing to write.
      if (had.created) into.delete(id);
      else into.set(id, { created: false, deleted: true, fields: new Set() });
    } else if (ch.created) {
      // Re-created (undo of a delete): write it whole.
      into.set(id, { created: true, deleted: false, fields: new Set() });
    } else if (!had.deleted) {
      ch.fields.forEach((f) => had.fields.add(f));
    }
  }
  return into;
}

/**
 * The writes for `changes` against the records cache: `{ upserts, deletes }`.
 * A changed record is the CACHED record (which already holds every teammate's
 * change that arrived) with only this person's changed fields laid on top, so
 * the cache's own diff sends just those fields. `records` = the board's
 * records now; `cachedById` maps id → the cached record in the flat
 * `listRecords` shape.
 */
export function writesFor(records, changes, cachedById) {
  const recs = byId(records);
  const upserts = [];
  const deletes = [];
  for (const [id, ch] of changes) {
    if (ch.deleted) {
      if (cachedById.has(id)) deletes.push(id);
      continue;
    }
    const rec = recs.get(id);
    if (!rec) continue;
    const cached = cachedById.get(id);
    if (ch.created || !cached) {
      upserts.push(rec);
      continue;
    }
    const { record_type: cachedType, parent_id: cachedParent, sort_key: cachedSort, ...cachedData } = cached;
    if (!('id' in (rec.data || {}))) delete cachedData.id;
    const data = { ...cachedData };
    for (const f of ch.fields) {
      if (META_FIELDS.includes(f) || f === 'record_type') continue;
      if (f in (rec.data || {})) data[f] = rec.data[f];
      else delete data[f];
    }
    upserts.push({
      id,
      record_type: ch.fields.has('record_type') ? rec.record_type : cachedType,
      parent_id: ch.fields.has('parent_id') ? rec.parent_id : cachedParent ?? null,
      sort_key: ch.fields.has('sort_key') ? rec.sort_key : cachedSort ?? null,
      data,
    });
  }
  return { upserts, deletes };
}
